import { useCallback, useMemo } from 'react';
import { mergeWithDefaultsForView } from '../config/fieldVisibility';

/**
 * useTodoListProps
 * 
 * Assembles all props needed for the TodoListView component.
 * Reduces prop assembly clutter in the main SprintDetail component.
 */
const useTodoListProps = ({
  // Data
  tasks,
  statuses,
  localSprint,
  calculatedProgress,
  allSprints,
  allTags,
  
  // Nested
  nested,
  loadFullSprint,
  
  // Actions
  actions,
  crossSprintActions,
  handlers, // Status management handlers
  
  // Callbacks
  onSelectSprint,
  handleLocalSprintIconChange,
  handleRenameSprint,
  setDetailSidebarTaskId,
  setAttachmentsModalTaskId,
  
  // View state
  showArchived,
  showActionableOnly,
  todoViewMode,
  setTodoViewMode,
  todoSwimlaneGroupBy,
  setTodoSwimlaneGroupBy,
  // New: Swimlane setting (same as Kanban)
  todoSwimlane = 'none',
  // New: Show nested items toggle
  todoShowNestedItems = true,
  // Filters (from filtersPerView)
  todoFilters = {},
  // Sort (from sortSettingsPerView)
  todoSortBy = 'manual',
  
  // Shared ref for modal trigger (from SprintDetail)
  todoModalTriggerRef,
  // Selection for Fokus mode
  todoSelectedIds,
  setTodoSelectedIds,
  // Element visibility
  todoElementVisibility = {},
  // View navigation (for "Open in view" dropdown)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  // For adding missions to nested sprints
  onUpdateSprint,
  isLoadingNested = false,
}) => {
  const mergedTodoElementVisibility = useMemo(
    () => mergeWithDefaultsForView(todoElementVisibility, 'todolist'),
    [todoElementVisibility],
  );

  // Toggle selection handler
  // Options:
  //   addToSelection: boolean - if true, add/remove from existing selection (shift/ctrl click)
  //                             if false, replace selection with just this item (normal click)
  const handleToggleSelect = (taskId, options = {}) => {
    const { addToSelection = false } = options;
    
    setTodoSelectedIds(prev => {
      // Multi-select mode (shift/ctrl held): toggle this item in the set
      if (addToSelection) {
        const newSet = new Set(prev);
        if (newSet.has(taskId)) {
          newSet.delete(taskId);
        } else {
          newSet.add(taskId);
        }
        return newSet;
      }
      
      // Single-select mode (normal click): select only this item
      // If already selected, deselect it
      if (prev.size === 1 && prev.has(taskId)) {
        return new Set();
      }
      return new Set([taskId]);
    });
  };

  // Handle go to task in a specific view (view is already changed by caller)
  const handleGoToTaskInView = (taskId, viewType) => {
    // Dispatch event to focus the task in the target view
    // Uses the same event as MissionSearch for consistency
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('focusMission', {
        detail: { missionId: taskId, view: viewType }
      }));
    }, 100);
  };

  // Helper to scroll to and edit a newly created task
  const scrollToAndEditTask = useCallback((taskId) => {
    setTimeout(() => {
      const element = document.querySelector(`[data-todo-id="${taskId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        setTimeout(() => {
          const nameSpan = element.querySelector('.todo-item__name');
          if (nameSpan) {
            nameSpan.click();
            
            setTimeout(() => {
              const input = element.querySelector('.todo-item__input');
              if (input) {
                input.select();
              }
            }, 50);
          }
        }, 300);
      }
    }, 100);
  }, []);

  // Handler to add a mission to a specific sprint (for nested campaigns in Master view)
  const handleAddMissionToSprint = useCallback((sprintId) => {
    if (!sprintId) {
      // Root level - add to current sprint
      const taskId = actions.handleAddTask(null, null, 'task');
      if (taskId) {
        scrollToAndEditTask(taskId);
      }
      return taskId;
    }
    
    // Find the nested sprint
    const targetSprint = nested.nestedSprints?.find(s => s.id === sprintId) || allSprints?.find(s => s.id === sprintId);
    if (!targetSprint) {
      console.warn('[useTodoListProps] Sprint not found:', sprintId);
      return null;
    }
    
    // Create a new task for the nested sprint
    const now = new Date().toISOString();
    const taskId = `task-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const existingTasksCount = targetSprint.tasks?.length || 0;
    
    const newTask = {
      id: taskId,
      name: '',
      order: existingTasksCount + 1,
      dependsOn: [],
      status: 'pending',
      x: 100 + (existingTasksCount * 200),
      y: 100,
      createdAt: now,
      updatedAt: now,
      startedAt: null,
      completedAt: null,
      estimatedDurationMinutes: null,
      dueDate: null,
    };
    
    // Update the nested sprint with the new task
    const updatedSprint = {
      ...targetSprint,
      tasks: [...(targetSprint.tasks || []), newTask],
    };
    
    onUpdateSprint?.(updatedSprint);
    
    // Scroll to and edit the new task
    scrollToAndEditTask(taskId);
    
    return taskId;
  }, [actions, nested.nestedSprints, allSprints, onUpdateSprint, scrollToAndEditTask]);

  const onNavigateToParentMission = useCallback(
    ({ parentSprintId, parentMissionId }) => {
      const parentSprint = allSprints?.find((s) => s.id === parentSprintId);
      if (parentSprint && onSelectSprint) {
        onSelectSprint(parentSprint, { view: currentView || 'todolist', missionId: parentMissionId });
      }
    },
    [allSprints, onSelectSprint, currentView],
  );

  return {
    tasks,
    statuses,
    categories: actions.getAllCategories(),
    allTags,
    nestedSprints: nested.nestedSprints,
    allSprints,
    loadFullSprint,
    campaignId: localSprint?.id || '',
    campaignName: localSprint?.name || '',
    campaignIcon: localSprint?.icon,
    campaignProgress: calculatedProgress,
    breadcrumbPath: nested.breadcrumbPath,
    onNavigateToParent: nested.handleGoToParent,
    onSelectSprint,
    onSprintIconChange: handleLocalSprintIconChange,
    onRenameSprint: handleRenameSprint,
    onStatusChange: crossSprintActions.handleCrossSprintStatusChange,
    onDeleteTask: crossSprintActions.handleCrossSprintDelete,
    onPasteTasks: handlers?.handlePasteTasks,
    onAddTask: (groupIdOrStatus, category, nodeType, options) => 
      actions.handleAddTask(groupIdOrStatus, category, nodeType, options),
    onRenameTask: crossSprintActions.handleCrossSprintRename,
    onOpenAttachments: (taskId) => setAttachmentsModalTaskId(taskId),
    onOpenNestedSprint: nested.handleOpenNestedSprint,
    onOpenDetails: (taskId) => setDetailSidebarTaskId(taskId),
    onPriorityChange: crossSprintActions.handleCrossSprintPriorityChange,
    onDifficultyChange: crossSprintActions.handleCrossSprintDifficultyChange,
    onDueDateChange: crossSprintActions.handleCrossSprintDueDateChange,
    onIconChange: crossSprintActions.handleCrossSprintIconChange,
    onArchiveTask: crossSprintActions.handleCrossSprintArchive,
    onUnarchiveTask: crossSprintActions.handleCrossSprintUnarchive,
    onDependencyChange: actions.handleDependencyChange,
    onConditionsChange: actions.handleConditionsChange,
    onFocusTask: crossSprintActions.handleCrossSprintFocusTask,
    onAddSubtask: crossSprintActions.handleAddSubtaskAsMission,
    // Status management
    onAddStatus: handlers?.handleAddStatus,
    onDeleteStatus: handlers?.handleDeleteStatus,
    onRenameStatus: handlers?.handleRenameStatus,
    onSetStatusAutoSync: actions.handleSetStatusAutoSync,
    showArchived,
    showActionableOnly,
    viewMode: todoViewMode,
    onViewModeChange: setTodoViewMode,
    sortBy: todoSortBy,
    filters: todoFilters,
    swimlaneGroupBy: todoSwimlaneGroupBy,
    onSwimlaneGroupByChange: setTodoSwimlaneGroupBy,
    // New: Swimlane setting (same as Kanban)
    swimlaneSetting: todoSwimlane,
    // New: Show nested items toggle
    showNestedItems: todoShowNestedItems,
    // Pass the ref so TodoListView can register its modal trigger
    _todoModalTriggerRef: todoModalTriggerRef,
    // Selection for Fokus mode
    selectedTaskIds: todoSelectedIds,
    onToggleSelect: handleToggleSelect,
    // Element visibility
    elementVisibility: mergedTodoElementVisibility,
    // View navigation
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    onGoToTaskInView: handleGoToTaskInView,
    // Decision handlers - use cross-sprint versions to work with nested sprints
    onDecisionAnswerChange: crossSprintActions.handleCrossSprintDecisionAnswerChange,
    onDecisionOptionsChange: crossSprintActions.handleCrossSprintDecisionOptionsChange,
    // For adding missions to nested sprints in Master view
    onAddMissionToSprint: handleAddMissionToSprint,
    localSprint,
    // Custom fields
    customFields: localSprint?.customFields || [],
    onCustomFieldValueChange: actions.handleCustomFieldValueChange,
    // Deliverables warning modal
    onSubmitDeliverable: actions.onSubmitDeliverable,
    onNavigateToParentMission,
    isLoadingNested,
  };
};

export default useTodoListProps;
